import { HigherApiClient } from 'src/api/client/ApiClient'
import { apiOps } from 'src/api/defs/apiOps'
import { Coordinates } from 'src/api/defs/Coordinates.type'
import { ClientWarehouseListItem } from 'src/api/defs/ClientWarehouseListItem.type'
import { BoxGroup } from 'src/api/defs/BoxGroup.type'
import { WarehousePodorys } from 'src/api/defs/WarehousePodorys.type'
import { SubscribeToNewFreeBoxBody } from '../../api/defs/SubscribeToNewFreeBoxBody.type'
import { BooleanValue } from 'src/api/defs/BooleanValue.type'
import { getEnvConfVal } from '../../conf/getEnvConfVal'
import { ClientWarehouseWithPrices } from '../../api/defs/ClientWarehouseWithPrices.type'
import { Infomessage } from 'src/api/defs/Infomessage.type'
import { BoxConsumerPrice } from 'src/api/defs/BoxConsumerPrice.type'
import { Discount } from 'src/api/defs/Discount.type'
import { PromoDiscountEffect } from 'src/api/defs/PromoDiscountEffect.type'
import { VatPrice } from 'src/api/defs/VatPrice.type'

const ops = apiOps.Warehouse
const foreignPriceScaleFixRatio = 100

const normalizeVatPrice = (value: VatPrice): VatPrice => ({
    ...value,
    price: {
        ...value.price,
        value: value.price.value / foreignPriceScaleFixRatio
    },
    vat: {
        ...value.vat,
        value: value.vat.value / foreignPriceScaleFixRatio
    },
    priceWithVat: {
        ...value.priceWithVat,
        value: value.priceWithVat.value / foreignPriceScaleFixRatio
    }
})

const normalizeDiscount = (value: Discount): Discount => ({
    ...value,
    fromInvoicingType: normalizeVatPrice(value.fromInvoicingType),
    fromPromo: normalizeVatPrice(value.fromPromo),
    total: normalizeVatPrice(value.total)
})

const normalizePromo = (value: PromoDiscountEffect): PromoDiscountEffect => ({
    ...value,
    before: normalizeVatPrice(value.before),
    after: normalizeVatPrice(value.after)
})

const normalizeBoxConsumerPrice = (
    value: BoxConsumerPrice
): BoxConsumerPrice => ({
    ...value,
    original: normalizeVatPrice(value.original),
    discount: normalizeDiscount(value.discount),
    total: normalizeVatPrice(value.total),
    promo: normalizePromo(value.promo)
})

const normalizeForeignWarehousePrices = (
    warehouse: ClientWarehouseListItem,
    currentCountry: string
): ClientWarehouseListItem => {
    if (warehouse.country === currentCountry) return warehouse

    return {
        ...warehouse,
        boxFrom: warehouse.boxFrom
            ? {
                  ...warehouse.boxFrom,
                  priceFrom: normalizeBoxConsumerPrice(
                      warehouse.boxFrom.priceFrom
                  )
              }
            : warehouse.boxFrom,
        boxTo: warehouse.boxTo
            ? {
                  ...warehouse.boxTo,
                  priceTo: normalizeBoxConsumerPrice(warehouse.boxTo.priceTo)
              }
            : warehouse.boxTo
    }
}

const normalizeWarehouseListPrices = (items: ClientWarehouseListItem[]) => {
    const currentCountry = getEnvConfVal('BOXYLAND_COUNTRY')
    return items.map(item =>
        normalizeForeignWarehousePrices(item, currentCountry)
    )
}

const sortWarehousesForListing = (
    items: ClientWarehouseListItem[]
): ClientWarehouseListItem[] =>
    items.sort((a, b) => {
        const deactivatedDiff =
            Number(a.deactivation.deactivated) -
            Number(b.deactivation.deactivated)
        if (deactivatedDiff !== 0) return deactivatedDiff

        return a.distance - b.distance
    })

export class WarehouseApi {
    constructor(private api: HigherApiClient) {}

    allCountry = (): Promise<ClientWarehouseListItem[]> => {
        return this.api
            .do<ClientWarehouseListItem[]>(ops.getAllCountry)
            .then(normalizeWarehouseListPrices)
    }

    aroundCountry = async (
        center: Coordinates
    ): Promise<ClientWarehouseListItem[]> => {
        return this.api
            .do<ClientWarehouseListItem[]>(ops.aroundCountry, {
                queryParams: center
            })
            .then(normalizeWarehouseListPrices)
            .then(sortWarehousesForListing)
    }

    all = (): Promise<ClientWarehouseListItem[]> => {
        const promises = [
            this.api.do<ClientWarehouseListItem[]>(ops.getAll).then(
                value => ({
                    status: 'fulfilled',
                    value
                }),
                reason => ({
                    status: 'rejected',
                    reason
                })
            )
        ]
        const proxies = getEnvConfVal('PROXY')
            ?.split(',')
            ?.map(locale => locale.trim())
            ?.slice(1)

        if (proxies.length > 0) {
            proxies.forEach((_, idx) => {
                promises.push(
                    this.api
                        .do<ClientWarehouseListItem[]>(ops.getAll, {
                            anotherDomainApi:
                                'api' + Number(proxies.length - idx + 1)
                        })
                        .then(
                            value => ({
                                status: 'fulfilled',
                                value
                            }),
                            reason => ({
                                status: 'rejected',
                                reason
                            })
                        )
                )
            })
        }
        return Promise.all(promises).then(valArray => {
            let retValDefault: ClientWarehouseListItem[] = []
            let retVal: ClientWarehouseListItem[] = []

            for (const result of valArray) {
                const indexOfApi = valArray.indexOf(result)
                if (result.status === 'fulfilled') {
                    const { value } = result as {
                        status: string
                        value: ClientWarehouseListItem[]
                    }
                    if (indexOfApi === 0) {
                        retValDefault = [...retValDefault, ...value]
                    } else {
                        retVal = [...retVal, ...value]
                    }
                }
            }

            return normalizeWarehouseListPrices(
                sortWarehousesForListing(retValDefault.concat(retVal))
            )
        })
    }

    around = async (
        center: Coordinates
    ): Promise<ClientWarehouseListItem[]> => {
        const proxies = getEnvConfVal('PROXY')
            ?.split(',')
            ?.map(locale => locale.trim())
            ?.slice(1)

        // const proxy3 = getEnvConfVal('PROXY_3')

        console.log('proxies: ', proxies)

        const promises = [
            this.api
                .do<ClientWarehouseListItem[]>(ops.around, {
                    queryParams: center
                })
                .then(
                    value => ({
                        status: 'fulfilled',
                        value
                    }),
                    reason => ({
                        status: 'rejected',
                        reason
                    })
                )
        ]

        if (proxies.length > 0) {
            proxies.forEach((_, idx) => {
                promises.push(
                    this.api
                        .do<ClientWarehouseListItem[]>(ops.around, {
                            queryParams: center,
                            anotherDomainApi:
                                'api' + Number(proxies.length - idx + 1)
                        })
                        .then(
                            value => ({
                                status: 'fulfilled',
                                value
                            }),
                            reason => ({
                                status: 'rejected',
                                reason
                            })
                        )
                )
            })
        }

        // if (proxy2.length > 0) {
        //     promises.push(
        //         this.api.do<ClientWarehouseListItem[]>(ops.around, {
        //             queryParams: center,
        //             anotherDomainApi: 'api2'
        //         })
        //     )
        // }

        // if (proxy3.length > 0) {
        //     promises.push(
        //         this.api.do<ClientWarehouseListItem[]>(ops.around, {
        //             queryParams: center,
        //             anotherDomainApi: 'api3'
        //         })
        //     )
        // }

        console.log('promises: ', promises)

        return Promise.all(promises).then(function (valArray) {
            console.log('valArray: ', valArray)
            let retValDefault: ClientWarehouseListItem[] = []
            let retVal: ClientWarehouseListItem[] = []

            for (const result of valArray) {
                console.log('result: ', result)
                const indexOfApi = valArray.indexOf(result)
                const { value } = result as {
                    status: string
                    value: ClientWarehouseListItem[]
                }
                console.log('indexOfApi: ', indexOfApi)
                console.log('value: ', value)
                if (result.status === 'fulfilled') {
                    if (indexOfApi === 0) {
                        retValDefault = [...retValDefault, ...value]
                    } else {
                        retVal = [...retVal, ...value]
                    }
                }
            }

            console.log('retValDefault: ', retValDefault)
            console.log('retVal: ', retVal)

            // sort main country boxes by deactivated
            retValDefault = retValDefault.sort((a, b) => {
                return (
                    Number(a.deactivation.deactivated) -
                    Number(b.deactivation.deactivated)
                )
            })

            // sort other countries boxes by deactivated
            retVal = retVal.sort((a, b) => {
                return (
                    Number(a.deactivation.deactivated) -
                    Number(b.deactivation.deactivated)
                )
            })

            return normalizeWarehouseListPrices(retValDefault.concat(retVal))
        })
    }

    detail = (id: number): Promise<ClientWarehouseWithPrices> =>
        this.api.doForId<ClientWarehouseWithPrices>(ops.readDetail, id)

    recommended = (id: number): Promise<ClientWarehouseListItem[]> =>
        this.api.doForId<ClientWarehouseListItem[]>(ops.recommended, id)

    boxes = (id: number): Promise<BoxGroup[]> => this.api.doForId(ops.boxes, id)

    podorys = (id: number): Promise<WarehousePodorys> =>
        this.api.doForId(ops.podorys, id)

    subscribeToNewFreeBox = (id: number, body: SubscribeToNewFreeBoxBody) =>
        this.api.do<BooleanValue>(ops.subscribeToNewFreeBox, {
            pathParams: { id },
            data: body
        })

    infoMessages = (id: number): Promise<Infomessage[]> =>
        this.api.doForId<Infomessage[]>(ops.infoMessages, id)
}
